import { endOfDay, startOfDay } from 'date-fns'
import { action, computed, observable } from 'mobx'
import { type FilterViewModel } from '@/infra/FiltersViewModel'

/**
 * Date range a {@link DateRangeFilter} filters by. Either end may be open.
 */
export interface DateRange {
  from: Date | null
  to: Date | null
}

/**
 * Filter for a date range.
 */
export class DateRangeFilter<TKey> implements FilterViewModel<TKey, DateRange> {
  /** @inheritDoc */
  key: TKey

  /** @inheritDoc */
  title: string

  /** @inheritDoc */
  @observable accessor value: DateRange | null = null

  /** @inheritDoc */
  @observable accessor editing = false

  constructor(key: TKey, title: string) {
    this.key = key
    this.title = title
  }

  /** @inheritDoc */
  @computed
  get valid() {
    return Boolean(this.value?.from ?? this.value?.to)
  }

  /** @inheritDoc */
  @computed
  get valueText() {
    const from = this.value?.from?.toLocaleDateString()
    const to = this.value?.to?.toLocaleDateString()
    if (from && to) {
      return `${from} - ${to}`
    }
    if (from) {
      return `from ${from}`
    }

    return to ? `to ${to}` : ''
  }

  /** @inheritDoc */
  toString() {
    if (!this.value?.from && !this.value?.to) {
      return ''
    }

    if (!this.value?.from) {
      return `${this.title} to: ${this.value?.to?.toLocaleDateString()}`
    }
    if (!this.value?.to) {
      return `${this.title} from: ${this.value?.from?.toLocaleDateString()}`
    }

    return `${
      this.title
    }: ${this.value?.from?.toLocaleDateString()} - ${this.value?.to?.toLocaleDateString()}`
  }

  /**
   * Sets the end of the range to the end of the given day.
   *
   * @param val
   */
  @action.bound
  setTo(val: Date) {
    this.value = {
      from: this.value?.from ?? null,
      to: val ? endOfDay(val) : null,
    }
  }

  /**
   * Sets the start of the range to the start of the given day.
   *
   * @param val
   */
  @action.bound
  setFrom(val: Date) {
    this.value = {
      from: val ? startOfDay(val) : null,
      to: this.value?.to ?? null,
    }
  }

  /** @inheritDoc */
  @action.bound
  clearValue() {
    this.value = null
  }

  /** @inheritDoc */
  @action.bound
  startEditing() {
    this.editing = true
  }

  /** @inheritDoc */
  @action.bound
  stopEditing() {
    this.editing = false
  }

  /**
   * Clears the value and closes the editor.
   */
  @action.bound
  clear() {
    this.clearValue()
    this.stopEditing()
  }

  /**
   * Ranges are not persisted.
   */
  serialize() {
    return null
  }

  /**
   * Restores the range from storage.
   */
  deserialize() {
    // Ranges are not persisted, so there is nothing to restore.
  }
}
